import React from "react";
import { Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Database, MessageSquare, Mic, Mail, CreditCard, CalendarClock, ShieldCheck } from "lucide-react";
import { StatusBadge, FullPageLoader } from "@/components/ds";
import { api } from "@/lib/api";

// Phase 1: read-only Platform Infrastructure overview (Senior/Platform Admin only — gated by the
// internal portal). Statuses reflect ACTUAL current configuration; providers without verified
// credentials are honestly shown as "Configuration Required" (no fake "Connected").
// Phase 2 will replace these with live authenticated health checks.
const PROVIDERS = [
  { key: "database", category: "Database", name: "MongoDB", icon: Database, tone: "green", status: "Operational", note: "Application database (MONGO_URL) — serving live requests." },
  { key: "billing", category: "Billing", name: "Stripe", icon: CreditCard, tone: "blue", status: "Configured — Test mode", note: "Checkout, subscriptions, setup fees, webhooks & payment verification active." },
  { key: "email", category: "Email", name: "Resend", icon: Mail, tone: "blue", status: "Configured", note: "Activation, secure payment links & notifications (Emergent-managed)." },
  { key: "telephony", category: "Telephony", name: "Twilio", icon: MessageSquare, tone: "amber", status: "Configuration Required", note: "Awaiting ResFil parent Twilio credentials + Trust Hub / A2P." },
  { key: "ai_voice", category: "AI Voice", name: "Voice Provider", icon: Mic, tone: "amber", status: "Configuration Required", note: "Awaiting verified voice-provider credentials." },
  { key: "google", category: "Google Services", name: "Google", icon: CalendarClock, tone: "slate", status: "Configuration Required", note: "Platform Google OAuth app config (separate from customers' own Google Calendar)." },
] as const;

export default function Infrastructure() {
  // Platform Infrastructure is platform-admin ONLY. Sales reps (who can reach other /internal
  // routes) and customers must never see ResFil's own provider config.
  const { data, isLoading } = useQuery({ queryKey: ["internal-me-infra"], queryFn: api.internalMe, retry: false });
  if (isLoading) return <FullPageLoader />;
  if (!data?.is_platform_admin) return <Navigate to="/internal/sales/me" replace />;
  const operational = PROVIDERS.filter((p) => p.status === "Operational").length;
  return (
    <div className="space-y-6" data-testid="platform-infrastructure">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Platform Infrastructure</h1>
        <p className="text-sm text-slate-500">The providers that power ResFil itself. These are ResFil platform credentials — never customer integrations. {operational}/{PROVIDERS.length} operational.</p>
      </div>
      <div className="rounded-xl border border-slate-200 bg-white">
        {PROVIDERS.map((p) => (
          <div key={p.key} className="flex items-start gap-3 border-b border-slate-50 px-5 py-4 last:border-0" data-testid={`infra-${p.key}`}>
            <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-600"><p.icon className="h-[18px] w-[18px]" /></span>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{p.category}</p>
              <p className="text-sm font-medium text-slate-900">{p.name}</p>
              <p className="mt-0.5 text-xs text-slate-500">{p.note}</p>
            </div>
            <StatusBadge tone={p.tone as any} data-testid={`infra-status-${p.key}`}>{p.status}</StatusBadge>
          </div>
        ))}
      </div>
      <p className="flex items-center gap-1.5 text-xs text-slate-400"><ShieldCheck className="h-3.5 w-3.5" /> Secrets stay server-side and are never exposed here. Live health checks & per-provider config arrive in Phase 2.</p>
    </div>
  );
}
